import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesRootProps = ComponentPropsWithoutRef<'section'>

const MedicationPhotoFilesRoot = ({ children, ...props }: MedicationPhotoFilesRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles.Title
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesTitleProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoFilesTitle = ({ children, ...props }: MedicationPhotoFilesTitleProps) => (
  <StTitle type='BodyMediumBold' {...props}>
    {children}
  </StTitle>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles.Item
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesItemProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoFilesItem = ({ children, ...props }: MedicationPhotoFilesItemProps) => (
  <StItem {...props}>{children}</StItem>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles.Name
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesNameProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoFilesName = ({ children, ...props }: MedicationPhotoFilesNameProps) => (
  <StName type='BodyMediumRegular' {...props}>
    {children}
  </StName>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles.Timestamp
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesTimestampProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoFilesTimestamp = ({ children, ...props }: MedicationPhotoFilesTimestampProps) => (
  <StTimestamp type='BodySmallRegular' {...props}>
    {children}
  </StTimestamp>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoFiles.Note
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoFilesNoteProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoFilesNote = ({ children, ...props }: MedicationPhotoFilesNoteProps) => (
  <StNote type='BodySmallRegular' {...props}>
    {children}
  </StNote>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationPhotoFiles = Object.assign(MedicationPhotoFilesRoot, {
  Title: MedicationPhotoFilesTitle,
  Item: MedicationPhotoFilesItem,
  Name: MedicationPhotoFilesName,
  Timestamp: MedicationPhotoFilesTimestamp,
  Note: MedicationPhotoFilesNote,
})

export { MedicationPhotoFiles }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ theme }) => `
    border: 1px solid ${theme.Colors.Neutrals.Light};
    border-radius: ${theme.UI.BorderRadius.Small};
  `}
`

const StTitle = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};

  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx.Space4};
    background: ${theme.Colors.Neutrals.Lighter};
  `}
`

const StItem = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space3};
    padding: ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4};
  `}
`

const StName = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StTimestamp = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
`

const StNote = styled(Span)`
  color: ${({ theme }) => theme.Colors.Theme.Primary};

  ${({ theme }) => `
    padding: ${theme.UI.SpacingPx.Space4};
    background: ${theme.Components.Chip.Primary.Fill};
  `}
`
